import type { Circuit, PlayerTrackPosition } from '../types/track.js';
import { arrivalStampsForTurnOrder } from './meetingStartOrder.js';
import { buildRaceRanking, buildRaceRankingEntries, turnOrderFromRanking } from './raceRanking.js';
import { isStartSideOnSfOverlap } from './circuitLoop.js';
import { trackPositionGridKey } from './trackMovement.js';
import { compareTrackProgress } from './trackProgress.js';

export interface TurnOrderPlayerProgress {
  position: PlayerTrackPosition;
  lapCount: number;
  arrivalStamp: number;
  /** Lancers de dé cumulés — tie-break aligné sur le classement live. */
  totalDiceRolls?: number;
  /** Côté « départ » sur boucle S/F (index 0 vs rival à pathLen-1). */
  startSideOnSfOverlap?: boolean;
  skipStartLineCrossingsRemaining?: number;
  hadStartLineSkipAtStart?: boolean;
}

/** Priorité de tour après dépassement réussi sur la même case (tant que co-localisés). */
export interface SameCellOvertakePair {
  attackerIndex: number;
  defenderIndex: number;
  /**
   * Partage d’ouverture (spec §6, pas de duel) : exemption colocataire /
   * invariant d’occupation, mais **pas** de priorité de tour (tampon d’arrivée).
   */
  openingStack?: boolean;
}

export function registerSameCellOvertake(
  pairs: readonly SameCellOvertakePair[],
  attackerIndex: number,
  defenderIndex: number,
  options?: { openingStack?: boolean }
): SameCellOvertakePair[] {
  return [
    ...pairs.filter(
      (p) =>
        p.attackerIndex !== attackerIndex &&
        p.defenderIndex !== defenderIndex &&
        p.attackerIndex !== defenderIndex
    ),
    {
      attackerIndex,
      defenderIndex,
      ...(options?.openingStack ? { openingStack: true as const } : {}),
    },
  ];
}

export function pruneSameCellOvertakePairs(
  circuit: Circuit,
  pairs: readonly SameCellOvertakePair[],
  getPosition: (playerIndex: number) => PlayerTrackPosition,
  getLapCount?: (playerIndex: number) => number
): SameCellOvertakePair[] {
  return pairs.filter((p) => {
    const pa: TurnOrderPlayerProgress = {
      position: getPosition(p.attackerIndex),
      lapCount: getLapCount?.(p.attackerIndex) ?? 0,
      arrivalStamp: 0,
    };
    const pd: TurnOrderPlayerProgress = {
      position: getPosition(p.defenderIndex),
      lapCount: getLapCount?.(p.defenderIndex) ?? 0,
      arrivalStamp: 0,
    };
    return areCoLocalizedForTurnOrder(circuit, pa, pd);
  });
}

function shareSamePhysicalCell(
  circuit: Circuit,
  a: TurnOrderPlayerProgress,
  b: TurnOrderPlayerProgress
): boolean {
  const ka = trackPositionGridKey(circuit, a.position);
  const kb = trackPositionGridKey(circuit, b.position);
  return ka != null && ka === kb;
}

/**
 * Même coordonnée grille mais indices path de boucle S/F (ex. Hill Valley : 0 et 66).
 * La priorité « dépassement même case » ne s’applique pas : l’ordre suit la progression.
 */
function isStartFinishOverlapPair(
  circuit: Circuit,
  a: TurnOrderPlayerProgress,
  b: TurnOrderPlayerProgress
): boolean {
  if (!shareSamePhysicalCell(circuit, a, b)) return false;
  if (a.position.jokerBranchIndex != null || b.position.jokerBranchIndex != null) return false;

  const pathLen = circuit.path.length;
  if (pathLen < 2) return false;
  const start = circuit.path[0];
  const end = circuit.path[pathLen - 1];
  if (start.x !== end.x || start.y !== end.y) return false;

  const ia = a.position.mainPathIndex;
  const ib = b.position.mainPathIndex;
  return (ia === 0 && ib === pathLen - 1) || (ia === pathLen - 1 && ib === 0);
}

export function registerSameCellPairsFromSuccessfulOvertakes(
  circuit: Circuit,
  pairs: readonly SameCellOvertakePair[],
  attackerIndex: number,
  attackerPosition: PlayerTrackPosition,
  overtakes: readonly {
    defenderIndex: number;
    success: boolean;
    openingStack?: boolean;
  }[],
  getDefenderPosition: (defenderIndex: number) => PlayerTrackPosition
): SameCellOvertakePair[] {
  const attackerKey = trackPositionGridKey(circuit, attackerPosition);
  if (!attackerKey) return [...pairs];

  let result = [...pairs];
  for (const overtake of overtakes) {
    if (!overtake.success) continue;
    const defenderKey = trackPositionGridKey(circuit, getDefenderPosition(overtake.defenderIndex));
    if (defenderKey != null && defenderKey === attackerKey) {
      result = registerSameCellOvertake(result, attackerIndex, overtake.defenderIndex, {
        openingStack: overtake.openingStack === true,
      });
    }
  }
  return result;
}

function shareSamePathCellForOvertakePriority(
  circuit: Circuit,
  a: TurnOrderPlayerProgress,
  b: TurnOrderPlayerProgress
): boolean {
  if (!shareSamePhysicalCell(circuit, a, b)) return false;
  if (isStartFinishOverlapPair(circuit, a, b)) return false;
  if (a.position.mainPathIndex !== b.position.mainPathIndex) return false;
  if ((a.position.jokerBranchIndex ?? null) !== (b.position.jokerBranchIndex ?? null)) {
    return false;
  }
  return true;
}

function areCoLocalizedForTurnOrder(
  circuit: Circuit,
  a: TurnOrderPlayerProgress,
  b: TurnOrderPlayerProgress
): boolean {
  return shareSamePathCellForOvertakePriority(circuit, a, b);
}

/** Réordonne pour respecter les paires attaquant → défenseur sur une même case. */
export function applySameCellOvertakeOrdering(
  circuit: Circuit,
  ordered: readonly number[],
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  pairs: readonly SameCellOvertakePair[]
): number[] {
  if (pairs.length === 0) return [...ordered];

  const result = [...ordered];
  let changed = true;
  while (changed) {
    changed = false;
    for (const pair of pairs) {
      const attacker = pair.attackerIndex;
      const defender = pair.defenderIndex;
      const pa = getProgress(attacker);
      const pd = getProgress(defender);
      if (!areCoLocalizedForTurnOrder(circuit, pa, pd)) continue;

      const attackerSlot = result.indexOf(attacker);
      const defenderSlot = result.indexOf(defender);
      if (attackerSlot < 0 || defenderSlot < 0 || attackerSlot < defenderSlot) continue;

      result.splice(attackerSlot, 1);
      const insertAt = result.indexOf(defender);
      result.splice(insertAt, 0, attacker);
      changed = true;
    }
  }
  return result;
}

function sameCellOvertakeOrder(
  circuit: Circuit,
  playerA: number,
  playerB: number,
  a: TurnOrderPlayerProgress,
  b: TurnOrderPlayerProgress,
  pairs: readonly SameCellOvertakePair[]
): number | null {
  const ka = trackPositionGridKey(circuit, a.position);
  const kb = trackPositionGridKey(circuit, b.position);
  if (!ka || ka !== kb) return null;
  if (!shareSamePathCellForOvertakePriority(circuit, a, b)) return null;

  for (const p of pairs) {
    if (p.openingStack) continue;
    if (playerA === p.attackerIndex && playerB === p.defenderIndex) return -1;
    if (playerA === p.defenderIndex && playerB === p.attackerIndex) return 1;
  }
  return null;
}

function isUniformDiceRollCount(
  playerIndices: readonly number[],
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  isFinished?: (playerIndex: number) => boolean
): boolean {
  const rolls = playerIndices
    .filter((i) => !(isFinished?.(i) ?? false))
    .map((i) => getProgress(i).totalDiceRolls ?? 0);
  if (rolls.length <= 1) return true;
  return Math.min(...rolls) === Math.max(...rolls);
}

/**
 * Ordre de jeu en début de manche : progression sur le tracé, puis lancers de dé (classement live),
 * puis tampon d’arrivée. Le classement n’utilise pas les paires « dépassement même case ».
 */
export function compareProgressForTurnOrder(
  circuit: Circuit,
  playerA: number,
  playerB: number,
  a: TurnOrderPlayerProgress,
  b: TurnOrderPlayerProgress,
  sameCellOvertakes: readonly SameCellOvertakePair[] = [],
  allowDiceTiebreak = true
): number {
  const overtakeCmp = sameCellOvertakeOrder(circuit, playerA, playerB, a, b, sameCellOvertakes);
  if (overtakeCmp != null) return overtakeCmp;

  const cmp = compareTrackProgress(
    circuit,
    {
      position: a.position,
      lapCount: a.lapCount,
      totalDiceRolls: a.totalDiceRolls,
      startSideOnSfOverlap: a.startSideOnSfOverlap,
      skipStartLineCrossingsRemaining: a.skipStartLineCrossingsRemaining,
      hadStartLineSkipAtStart: a.hadStartLineSkipAtStart,
    },
    {
      position: b.position,
      lapCount: b.lapCount,
      totalDiceRolls: b.totalDiceRolls,
      startSideOnSfOverlap: b.startSideOnSfOverlap,
      skipStartLineCrossingsRemaining: b.skipStartLineCrossingsRemaining,
      hadStartLineSkipAtStart: b.hadStartLineSkipAtStart,
    }
  );
  if (cmp !== 0) return cmp;
  const diceA = a.totalDiceRolls ?? 0;
  const diceB = b.totalDiceRolls ?? 0;
  if (allowDiceTiebreak && diceA !== diceB) return diceA - diceB;
  return a.arrivalStamp - b.arrivalStamp;
}

function withProgressFlags(
  circuit: Circuit,
  playerIndices: readonly number[],
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  /** Tous les participants — requis pour la boucle S/F quand seuls les actifs sont triés. */
  overlapPlayerIndices?: readonly number[]
): (playerIndex: number) => TurnOrderPlayerProgress {
  const overlapScope = overlapPlayerIndices ?? playerIndices;
  return (playerIndex: number) => {
    const base = getProgress(playerIndex);
    return {
      ...base,
      startSideOnSfOverlap: isStartSideOnSfOverlap(
        circuit,
        playerIndex,
        base.position,
        base.lapCount,
        (i) => getProgress(i).position,
        (i) => getProgress(i).lapCount,
        overlapScope
      ),
    };
  };
}

export function sortPlayerIndicesByProgress(
  circuit: Circuit,
  playerIndices: number[],
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  _sameCellOvertakes: readonly SameCellOvertakePair[] = [],
  isFinished?: (playerIndex: number) => boolean,
  allPlayerIndices?: readonly number[]
): number[] {
  const allowDiceTiebreak = isUniformDiceRollCount(playerIndices, getProgress, isFinished);
  const getProgressWithFlags = withProgressFlags(
    circuit,
    playerIndices,
    getProgress,
    allPlayerIndices
  );
  const base = [...playerIndices].sort((a, b) => {
    const pa = getProgressWithFlags(a);
    const pb = getProgressWithFlags(b);
    const cmp = compareTrackProgress(
      circuit,
      {
        position: pa.position,
        lapCount: pa.lapCount,
        totalDiceRolls: pa.totalDiceRolls,
        startSideOnSfOverlap: pa.startSideOnSfOverlap,
        skipStartLineCrossingsRemaining: pa.skipStartLineCrossingsRemaining,
        hadStartLineSkipAtStart: pa.hadStartLineSkipAtStart,
      },
      {
        position: pb.position,
        lapCount: pb.lapCount,
        totalDiceRolls: pb.totalDiceRolls,
        startSideOnSfOverlap: pb.startSideOnSfOverlap,
        skipStartLineCrossingsRemaining: pb.skipStartLineCrossingsRemaining,
        hadStartLineSkipAtStart: pb.hadStartLineSkipAtStart,
      }
    );
    if (cmp !== 0) return cmp;
    const diceA = pa.totalDiceRolls ?? 0;
    const diceB = pb.totalDiceRolls ?? 0;
    if (allowDiceTiebreak && diceA !== diceB) return diceA - diceB;
    return pa.arrivalStamp - pb.arrivalStamp;
  });
  return base;
}

export function alignTurnStateFromSlot(
  turnOrder: number[],
  turnSlot: number,
  isFinished: (playerIndex: number) => boolean
): { turnSlot: number; currentPlayerIndex: number } {
  if (turnOrder.length === 0) {
    return { turnSlot: 0, currentPlayerIndex: 0 };
  }

  const clamped = Math.max(0, Math.min(turnSlot, turnOrder.length - 1));
  const slotPlayer = turnOrder[clamped] ?? -1;
  if (slotPlayer >= 0 && !isFinished(slotPlayer)) {
    return { turnSlot: clamped, currentPlayerIndex: slotPlayer };
  }

  const next = findNextActiveTurnSlotWithoutWrap(turnOrder, clamped + 1, isFinished);
  if (next >= 0) {
    return { turnSlot: next, currentPlayerIndex: turnOrder[next] ?? 0 };
  }

  const fallback = findNextActiveTurnSlot(turnOrder, 0, isFinished);
  if (fallback >= 0) {
    return { turnSlot: fallback, currentPlayerIndex: turnOrder[fallback] ?? 0 };
  }

  return { turnSlot: clamped, currentPlayerIndex: turnOrder[clamped] ?? 0 };
}

export function normalizeTurnState(
  turnOrder: number[],
  turnSlot: number,
  currentPlayerIndex: number,
  isFinished: (playerIndex: number) => boolean
): { turnSlot: number; currentPlayerIndex: number } {
  if (turnOrder.length === 0) {
    return { turnSlot: 0, currentPlayerIndex };
  }

  const clampedSlot = Math.max(0, Math.min(turnSlot, turnOrder.length - 1));
  const slotPlayer = turnOrder[clampedSlot] ?? -1;

  // turnSlot fait foi tant que le joueur du slot est encore en course.
  if (slotPlayer >= 0 && !isFinished(slotPlayer)) {
    return { turnSlot: clampedSlot, currentPlayerIndex: slotPlayer };
  }

  if (currentPlayerIndex >= 0 && !isFinished(currentPlayerIndex)) {
    const playerSlot = turnOrder.indexOf(currentPlayerIndex);
    if (playerSlot >= 0) {
      return { turnSlot: playerSlot, currentPlayerIndex };
    }
  }

  return alignTurnStateFromSlot(turnOrder, clampedSlot, isFinished);
}

export function findNextActiveTurnSlot(
  turnOrder: number[],
  startSlot: number,
  isFinished: (playerIndex: number) => boolean
): number {
  for (let i = 0; i < turnOrder.length; i++) {
    const slot = (startSlot + i) % turnOrder.length;
    const playerIndex = turnOrder[slot] ?? -1;
    if (playerIndex >= 0 && !isFinished(playerIndex)) return slot;
  }
  return -1;
}

export function findNextActiveTurnSlotWithoutWrap(
  turnOrder: number[],
  startSlot: number,
  isFinished: (playerIndex: number) => boolean
): number {
  for (let slot = startSlot; slot < turnOrder.length; slot++) {
    const playerIndex = turnOrder[slot] ?? -1;
    if (playerIndex >= 0 && !isFinished(playerIndex)) return slot;
  }
  return -1;
}

/** Ordre de manche = classement live (actifs d’abord), identique qualif / demi / finale. */
export function recomputeTurnOrderFromLiveRanking(
  circuit: Circuit,
  numPlayers: number,
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  isFinished?: (playerIndex: number) => boolean,
  getFinishStamp?: (playerIndex: number) => number
): number[] {
  const finished = (playerIndex: number) => isFinished?.(playerIndex) ?? false;
  const entries = buildRaceRankingEntries(circuit, numPlayers, (playerIndex) => {
    const p = getProgress(playerIndex);
    return {
      finished: finished(playerIndex),
      finishStamp: getFinishStamp?.(playerIndex) ?? 0,
      lapCount: p.lapCount,
      mainPathIndex: p.position.mainPathIndex,
      jokerBranchIndex: p.position.jokerBranchIndex,
      startLaneIndex: p.position.startLaneIndex,
      arrivalStamp: p.arrivalStamp,
      totalDiceRolls: p.totalDiceRolls ?? 0,
      skipStartLineCrossingsRemaining: p.skipStartLineCrossingsRemaining,
      hadStartLineSkipAtStart: p.hadStartLineSkipAtStart,
    };
  });
  const ranked = buildRaceRanking(entries, { circuit });
  return turnOrderFromRanking(ranked, finished);
}

function roundStartTurnOrderFromLiveRanking(
  circuit: Circuit,
  numPlayers: number,
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  isFinished: (playerIndex: number) => boolean,
  getFinishStamp?: (playerIndex: number) => number,
  options?: { pendingDiceLayerFirst?: boolean }
): AdvanceTurnAfterMoveResult {
  const finished = (playerIndex: number) => isFinished(playerIndex);
  const entries = buildRaceRankingEntries(circuit, numPlayers, (playerIndex) => {
    const p = getProgress(playerIndex);
    return {
      finished: finished(playerIndex),
      finishStamp: getFinishStamp?.(playerIndex) ?? 0,
      lapCount: p.lapCount,
      mainPathIndex: p.position.mainPathIndex,
      jokerBranchIndex: p.position.jokerBranchIndex,
      startLaneIndex: p.position.startLaneIndex,
      arrivalStamp: p.arrivalStamp,
      totalDiceRolls: p.totalDiceRolls ?? 0,
      skipStartLineCrossingsRemaining: p.skipStartLineCrossingsRemaining,
      hadStartLineSkipAtStart: p.hadStartLineSkipAtStart,
    };
  });
  const ranked = buildRaceRanking(entries, { circuit });
  const byRank = [...ranked].sort((a, b) => a.rank - b.rank);
  const active = byRank.filter((r) => !finished(r.playerIndex));
  const done = byRank.filter((r) => finished(r.playerIndex));
  let turnOrder: number[];
  if (options?.pendingDiceLayerFirst && active.length > 1) {
    const rolls = active.map((r) => getProgress(r.playerIndex).totalDiceRolls ?? 0);
    const min = Math.min(...rolls);
    const max = Math.max(...rolls);
    if (min !== max) {
      const pending = active.filter(
        (r) => (getProgress(r.playerIndex).totalDiceRolls ?? 0) === min
      );
      const ahead = active.filter((r) => (getProgress(r.playerIndex).totalDiceRolls ?? 0) > min);
      turnOrder = [...pending, ...ahead, ...done].map((r) => r.playerIndex);
    } else {
      turnOrder = [...active, ...done].map((r) => r.playerIndex);
    }
  } else {
    turnOrder = [...active, ...done].map((r) => r.playerIndex);
  }
  const aligned = alignTurnStateForNewRound(turnOrder, isFinished);
  return {
    turnOrder,
    turnSlot: aligned.turnSlot,
    currentPlayerIndex: aligned.currentPlayerIndex,
    playerArrivalStamp: arrivalStampsForTurnOrder(numPlayers, turnOrder),
  };
}

export function recomputeTurnOrderFromProgress(
  circuit: Circuit,
  numPlayers: number,
  getProgress: (playerIndex: number) => TurnOrderPlayerProgress,
  sameCellOvertakes: readonly SameCellOvertakePair[] = [],
  isFinished?: (playerIndex: number) => boolean
): number[] {
  const all = Array.from({ length: numPlayers }, (_, i) => i);
  if (!isFinished) {
    return sortPlayerIndicesByProgress(
      circuit,
      all,
      getProgress,
      sameCellOvertakes,
      undefined,
      all
    );
  }
  const active = all.filter((i) => !isFinished(i));
  const inactive = all.filter((i) => isFinished(i));
  const sortedActive = sortPlayerIndicesByProgress(
    circuit,
    active,
    getProgress,
    sameCellOvertakes,
    isFinished,
    all
  );
  const sortedInactive =
    inactive.length > 0
      ? sortPlayerIndicesByProgress(
          circuit,
          inactive,
          getProgress,
          sameCellOvertakes,
          isFinished,
          all
        )
      : [];
  return [...sortedActive, ...sortedInactive];
}

export interface AdvanceTurnAfterMoveInput {
  circuit: Circuit;
  numPlayers: number;
  turnOrder: number[];
  turnSlot: number;
  /** Joueur qui vient de lancer — aligne turnSlot avant rotation (évite un tourSlot périmé). */
  moverPlayerIndex?: number;
  playerFinishedRace: boolean[];
  getPosition: (playerIndex: number) => PlayerTrackPosition;
  getLapCount: (playerIndex: number) => number;
  getArrivalStamp: (playerIndex: number) => number;
  getFinishStamp?: (playerIndex: number) => number;
  getTotalDiceRolls?: (playerIndex: number) => number;
  getSkipStartLineCrossingsRemaining?: (playerIndex: number) => number;
  getHadStartLineSkipAtStart?: (playerIndex: number) => boolean;
  sameCellOvertakes?: readonly SameCellOvertakePair[];
}

export interface AdvanceTurnAfterMoveResult {
  turnOrder: number[];
  turnSlot: number;
  currentPlayerIndex: number;
  /** Tampons réalignés sur le classement live en tête de manche. */
  playerArrivalStamp?: number[];
}

/** Tous les pilotes encore en course ont le même nombre de lancers (début de manche). */
export function isUniformDiceRollCountAmongActivePlayers(
  numPlayers: number,
  playerFinishedRace: readonly boolean[],
  getTotalDiceRolls: (playerIndex: number) => number
): boolean {
  const rolls: number[] = [];
  for (let i = 0; i < numPlayers; i++) {
    if (playerFinishedRace[i]) continue;
    rolls.push(getTotalDiceRolls(i));
  }
  if (rolls.length <= 1) return true;
  return Math.min(...rolls) === Math.max(...rolls);
}

function buildTurnOrderProgressGetter(
  input: Pick<
    AdvanceTurnAfterMoveInput,
    | 'getPosition'
    | 'getLapCount'
    | 'getArrivalStamp'
    | 'getTotalDiceRolls'
    | 'getSkipStartLineCrossingsRemaining'
    | 'getHadStartLineSkipAtStart'
  >
): (playerIndex: number) => TurnOrderPlayerProgress {
  return (playerIndex: number): TurnOrderPlayerProgress => ({
    position: input.getPosition(playerIndex),
    lapCount: input.getLapCount(playerIndex),
    arrivalStamp: input.getArrivalStamp(playerIndex),
    totalDiceRolls: input.getTotalDiceRolls?.(playerIndex) ?? 0,
    skipStartLineCrossingsRemaining: input.getSkipStartLineCrossingsRemaining?.(playerIndex),
    hadStartLineSkipAtStart: input.getHadStartLineSkipAtStart?.(playerIndex),
  });
}

/** État de tour en tête de manche : premier pilote actif dans l’ordre canonique. */
export function alignTurnStateForNewRound(
  turnOrder: number[],
  isFinished: (playerIndex: number) => boolean
): { turnSlot: number; currentPlayerIndex: number } {
  const firstActiveSlot = turnOrder.findIndex((playerIndex) => !isFinished(playerIndex));
  const turnSlot = firstActiveSlot >= 0 ? firstActiveSlot : 0;
  return {
    turnSlot,
    currentPlayerIndex: turnOrder[turnSlot] ?? 0,
  };
}

/**
 * Réaligne turnOrder / turnSlot / currentPlayerIndex.
 * - Début de manche (lancers uniformes) : ordre = classement live, tampons réalignés.
 * - Milieu de manche : turnSlot fait foi ; currentPlayerIndex = turnOrder[turnSlot].
 */
export function ensureTurnOrderConsistency(
  input: AdvanceTurnAfterMoveInput & {
    currentPlayerIndex: number;
    /**
     * Couche de lancers déjà dépassée (min des actifs > dernier alignement)
     * alors que les compteurs ne sont plus uniformes : réaligne quand même,
     * en faisant lancer d’abord les retardataires dans l’ordre du classement.
     */
    forceLiveRanking?: boolean;
  }
): AdvanceTurnAfterMoveResult {
  const {
    circuit,
    numPlayers,
    turnOrder,
    turnSlot,
    playerFinishedRace,
    getPosition,
    getLapCount,
    getArrivalStamp,
    getFinishStamp,
    getTotalDiceRolls,
    getSkipStartLineCrossingsRemaining,
    getHadStartLineSkipAtStart,
    forceLiveRanking,
  } = input;
  const isFinished = (playerIndex: number) => playerFinishedRace[playerIndex] ?? false;
  const getProgress = buildTurnOrderProgressGetter({
    getPosition,
    getLapCount,
    getArrivalStamp,
    getTotalDiceRolls,
    getSkipStartLineCrossingsRemaining,
    getHadStartLineSkipAtStart,
  });

  const atRoundStart = isUniformDiceRollCountAmongActivePlayers(
    numPlayers,
    playerFinishedRace,
    (playerIndex) => getTotalDiceRolls?.(playerIndex) ?? 0
  );

  if (circuit.path.length > 0 && (atRoundStart || forceLiveRanking)) {
    return roundStartTurnOrderFromLiveRanking(
      circuit,
      numPlayers,
      getProgress,
      isFinished,
      getFinishStamp,
      { pendingDiceLayerFirst: forceLiveRanking === true && !atRoundStart }
    );
  }

  if (turnOrder.length === 0) {
    return { turnOrder, turnSlot: 0, currentPlayerIndex: 0 };
  }

  const clampedSlot = Math.max(0, Math.min(turnSlot, turnOrder.length - 1));
  const slotPlayer = turnOrder[clampedSlot] ?? -1;
  if (slotPlayer >= 0 && !isFinished(slotPlayer)) {
    return { turnOrder, turnSlot: clampedSlot, currentPlayerIndex: slotPlayer };
  }

  const nextSlot = findNextActiveTurnSlotWithoutWrap(turnOrder, clampedSlot, isFinished);
  if (nextSlot >= 0) {
    return {
      turnOrder,
      turnSlot: nextSlot,
      currentPlayerIndex: turnOrder[nextSlot] ?? 0,
    };
  }

  if (circuit.path.length > 0) {
    return roundStartTurnOrderFromLiveRanking(
      circuit,
      numPlayers,
      getProgress,
      isFinished,
      getFinishStamp
    );
  }

  const normalized = normalizeTurnState(turnOrder, clampedSlot, slotPlayer, isFinished);
  return {
    turnOrder,
    turnSlot: normalized.turnSlot,
    currentPlayerIndex: normalized.currentPlayerIndex,
  };
}

export function advanceTurnAfterMove(input: AdvanceTurnAfterMoveInput): AdvanceTurnAfterMoveResult {
  const {
    circuit,
    numPlayers,
    turnSlot: inputTurnSlot,
    moverPlayerIndex,
    playerFinishedRace,
    getPosition,
    getLapCount,
    getArrivalStamp,
    getFinishStamp,
    getTotalDiceRolls,
    getSkipStartLineCrossingsRemaining,
    getHadStartLineSkipAtStart,
  } = input;
  const isFinished = (playerIndex: number) => playerFinishedRace[playerIndex] ?? false;
  const getProgress = (playerIndex: number): TurnOrderPlayerProgress => ({
    position: getPosition(playerIndex),
    lapCount: getLapCount(playerIndex),
    arrivalStamp: getArrivalStamp(playerIndex),
    totalDiceRolls: getTotalDiceRolls?.(playerIndex) ?? 0,
    skipStartLineCrossingsRemaining: getSkipStartLineCrossingsRemaining?.(playerIndex),
    hadStartLineSkipAtStart: getHadStartLineSkipAtStart?.(playerIndex),
  });

  let turnSlot = inputTurnSlot;
  if (moverPlayerIndex != null && moverPlayerIndex >= 0) {
    const moverSlot = input.turnOrder.indexOf(moverPlayerIndex);
    if (moverSlot >= 0) {
      turnSlot = moverSlot;
    }
  }

  if (playerFinishedRace.every(Boolean)) {
    return {
      turnOrder: input.turnOrder,
      turnSlot,
      currentPlayerIndex: input.turnOrder[turnSlot] ?? 0,
    };
  }

  const turnOrder = input.turnOrder;

  const nextWithoutWrap = findNextActiveTurnSlotWithoutWrap(turnOrder, turnSlot + 1, isFinished);
  if (nextWithoutWrap >= 0) {
    return {
      turnOrder,
      turnSlot: nextWithoutWrap,
      currentPlayerIndex: turnOrder[nextWithoutWrap] ?? 0,
    };
  }

  return roundStartTurnOrderFromLiveRanking(
    circuit,
    numPlayers,
    getProgress,
    isFinished,
    getFinishStamp
  );
}

export interface InitializeRaceTurnOrderInput {
  circuit: Circuit;
  numParticipants: number;
  /** Ordre de tie-break sur même case (qualif : tirage ; demi/finale : grille). */
  arrivalTiebreakOrder?: readonly number[];
  getProgress: (playerIndex: number) => Omit<TurnOrderPlayerProgress, 'arrivalStamp'>;
  sameCellOvertakes?: readonly SameCellOvertakePair[];
  isFinished?: (playerIndex: number) => boolean;
}

export interface InitializeRaceTurnOrderResult {
  turnOrder: number[];
  turnSlot: number;
  currentPlayerIndex: number;
  playerArrivalStamp: number[];
}

/**
 * Ordre de jeu au départ : tampons d’arrivée puis reclassement par progression sur le tracé.
 * Même mécanique qualif / demi / finale (seule la grille de départ diffère).
 */
export function initializeRaceTurnOrderFromGrid(
  input: InitializeRaceTurnOrderInput
): InitializeRaceTurnOrderResult {
  const n = input.numParticipants;
  const isFinished = input.isFinished ?? (() => false);
  const playerArrivalStamp =
    input.arrivalTiebreakOrder && input.arrivalTiebreakOrder.length === n
      ? arrivalStampsForTurnOrder(n, input.arrivalTiebreakOrder)
      : Array.from({ length: n }, (_, i) => i + 1);

  const getProgress = (playerIndex: number): TurnOrderPlayerProgress => ({
    ...input.getProgress(playerIndex),
    arrivalStamp: playerArrivalStamp[playerIndex] ?? playerIndex + 1,
  });

  const turnOrder = recomputeTurnOrderFromLiveRanking(input.circuit, n, getProgress, isFinished);
  const firstActive = turnOrder.findIndex((playerIndex) => !isFinished(playerIndex));
  const turnSlot = firstActive >= 0 ? firstActive : 0;
  return {
    turnOrder,
    turnSlot,
    currentPlayerIndex: turnOrder[turnSlot] ?? 0,
    playerArrivalStamp,
  };
}
